import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Tabs, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Loader2, MessageSquare, Save, Reply, Trash2, Archive, Send, History,
} from "lucide-react";
import { toast } from "sonner";
import { adminFetch, adminPatch, adminPost, adminDelete } from "@/lib/api";

const STATUS_OPTIONS = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "read", label: "Read" },
  { key: "replied", label: "Replied" },
  { key: "archived", label: "Archived" },
];

const STATUS_COLOR = {
  new: "bg-brand-soft text-brand border-brand/40",
  read: "bg-bone text-ink border-ink/15",
  replied: "bg-emerald-50 text-emerald-700 border-emerald-200",
  archived: "bg-ink/5 text-ink/55 border-ink/15",
};

const CRM_BADGE = {
  paid_registrant: { label: "Paid registrant", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  pending_registrant: { label: "Registration pending", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  lead: { label: "Lead", cls: "bg-brand-soft text-brand border-brand/40" },
};

function CrmBadge({ status }) {
  const def = CRM_BADGE[status];
  if (!def) return null;
  return (
    <span data-testid={`crm-badge-${status}`} className={`inline-flex items-center gap-1 text-[10px] font-mono-spec uppercase tracking-wider px-1.5 py-0.5 rounded border ${def.cls}`}>
      {def.label}
    </span>
  );
}

function fmt(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return ts;
  }
}

export default function Inquiries({ pw }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ inquiries: [], counts: {}, total: 0, new_count: 0 });
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [replyText, setReplyText] = useState("");
  const [replySubject, setReplySubject] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async (f = filter) => {
    setLoading(true);
    try {
      const path = f && f !== "all" ? `/admin/inquiries?status=${f}` : "/admin/inquiries";
      const res = await adminFetch(path, pw);
      setData(res);
    } catch {
      toast.error("Failed to load inquiries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(filter); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);

  const open = async (inq) => {
    setSelected(inq);
    setNotes(inq.admin_notes || "");
    setReplyText("");
    setReplySubject("Re: your message about the AI Build Lab");
    if (inq.status === "new") {
      try {
        await adminPatch(`/admin/inquiries/${inq.id}`, pw, { status: "read" });
        await load(filter);
      } catch { /* ignore */ }
    }
  };

  const close = () => { setSelected(null); setNotes(""); setReplyText(""); setReplySubject(""); };

  const setStatus = async (id, status) => {
    setBusy(true);
    try {
      await adminPatch(`/admin/inquiries/${id}`, pw, { status });
      toast.success(`Marked as ${status}.`);
      await load(filter);
      if (selected && selected.id === id) {
        setSelected((s) => ({ ...s, status }));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed.");
    } finally {
      setBusy(false);
    }
  };

  const saveNotes = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await adminPatch(`/admin/inquiries/${selected.id}`, pw, { admin_notes: notes });
      toast.success("Notes saved.");
      await load(filter);
    } catch {
      toast.error("Failed to save notes.");
    } finally {
      setBusy(false);
    }
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm("Delete this inquiry permanently?")) return;
    setBusy(true);
    try {
      await adminDelete(`/admin/inquiries/${id}`, pw);
      toast.success("Deleted.");
      close();
      await load(filter);
    } catch {
      toast.error("Delete failed.");
    } finally {
      setBusy(false);
    }
  };

  const sendReply = async () => {
    if (!selected) return;
    const msg = replyText.trim();
    if (msg.length < 2) {
      toast.error("Type your reply first.");
      return;
    }
    setSendingReply(true);
    try {
      const res = await adminPost(`/admin/inquiries/${selected.id}/reply`, pw, {
        message: msg,
        subject: replySubject.trim() || undefined,
      });
      toast.success(res?.email_id ? "Reply sent." : "Sent (mock — no Resend key in this env).");
      setReplyText("");
      await load(filter);
      const fresh = await adminFetch("/admin/inquiries", pw);
      const updated = (fresh.inquiries || []).find((i) => i.id === selected.id);
      if (updated) setSelected(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send reply.");
    } finally {
      setSendingReply(false);
    }
  };


  const counts = useMemo(() => ({
    new: data.counts?.new || 0,
    read: data.counts?.read || 0,
    replied: data.counts?.replied || 0,
    archived: data.counts?.archived || 0,
  }), [data.counts]);

  return (
    <div data-testid="inquiries-tab" className="space-y-5">
      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="bg-bone border border-[var(--line)]">
          {STATUS_OPTIONS.map((s) => (
            <TabsTrigger key={s.key} value={s.key} data-testid={`inq-filter-${s.key}`}>
              {s.label}{s.key !== "all" && counts[s.key] ? ` (${counts[s.key]})` : ""}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="rounded-2xl border border-[var(--line)] overflow-hidden bg-bone overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-[var(--line)] hover:bg-transparent">
              <TableHead className="text-ink/60 w-44">When</TableHead>
              <TableHead className="text-ink/60">From</TableHead>
              <TableHead className="text-ink/60">Message preview</TableHead>
              <TableHead className="text-ink/60 w-28">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow className="border-[var(--line)] hover:bg-transparent">
                <TableCell colSpan={4} className="text-center text-ink/50 py-10">
                  <Loader2 className="animate-spin inline-block mr-2" size={14} /> Loading…
                </TableCell>
              </TableRow>
            )}
            {!loading && data.inquiries.length === 0 && (
              <TableRow className="border-[var(--line)] hover:bg-transparent">
                <TableCell colSpan={4} className="text-center text-ink/50 py-12">
                  No inquiries in this view yet. When visitors submit the contact form on your site, they show up here.
                </TableCell>
              </TableRow>
            )}
            {!loading && data.inquiries.map((i) => (
              <TableRow
                key={i.id}
                data-testid={`inquiry-row-${i.id}`}
                className="border-[var(--line)] hover:bg-white cursor-pointer"
                onClick={() => open(i)}
              >
                <TableCell className="text-ink/65 text-sm font-mono-spec">{fmt(i.created_at)}</TableCell>
                <TableCell>
                  <div className="font-medium text-ink">{i.name}</div>
                  <div className="text-xs text-ink/55 truncate max-w-[220px]">{i.email}</div>
                </TableCell>
                <TableCell className="text-ink/75 text-sm max-w-[420px]">
                  <div className="truncate">{i.message}</div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={`${STATUS_COLOR[i.status] || ""} text-[10px] px-2 py-0.5 capitalize`}>
                    {i.status}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && close()}>
        <DialogContent className="bg-white border border-[var(--line)] text-ink max-w-2xl" data-testid="inquiry-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl tracking-[-0.025em] flex items-center gap-2 flex-wrap">
                  <MessageSquare size={18} className="text-brand" />
                  {selected.name}
                  <Badge variant="outline" className={`${STATUS_COLOR[selected.status] || ""} text-[10px] px-2 py-0.5 capitalize`}>
                    {selected.status}
                  </Badge>
                  <CrmBadge status={selected.crm_status} />
                </DialogTitle>
                <DialogDescription className="text-ink/65">
                  <a href={`mailto:${selected.email}`} className="text-brand hover:underline">{selected.email}</a>
                  <span className="mx-2">·</span>
                  <span>{fmt(selected.created_at)}</span>
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 pt-2">
                <div className="rounded-2xl bg-bone border border-[var(--line)] p-4 text-[15px] text-ink/85 whitespace-pre-wrap leading-relaxed" data-testid="inquiry-message">
                  {selected.message}
                </div>

                {/* Reply thread */}
                {selected.replies && selected.replies.length > 0 && (
                  <div data-testid="inquiry-thread" className="space-y-2">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-ink/60 font-mono-spec">
                      <History size={11} /> Your replies ({selected.replies.length})
                    </div>
                    {selected.replies.map((r, idx) => (
                      <div key={idx} data-testid={`inquiry-reply-${idx}`} className="rounded-xl border border-brand/25 bg-brand-soft/40 p-3">
                        <div className="text-[11px] text-ink/55 font-mono-spec uppercase tracking-wider mb-1 flex items-center justify-between">
                          <span>Sent {fmt(r.sent_at)}</span>
                          {r.mocked && <span className="text-amber-700">· mocked (no live email key)</span>}
                        </div>
                        <div className="text-sm text-ink/80 italic mb-1">{r.subject}</div>
                        <div className="text-sm text-ink/85 whitespace-pre-wrap leading-relaxed">{r.message}</div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Inline reply composer */}
                <div className="rounded-2xl border border-[var(--line)] p-4 bg-bone space-y-3" data-testid="inquiry-reply-composer">
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-ink/60 font-mono-spec">
                    <Reply size={11} /> Reply from the back office &mdash; sends from <span className="text-brand normal-case tracking-normal">{`AI Build Lab <hello@donjawilliams.com>`}</span>
                  </div>
                  <Input
                    data-testid="inquiry-reply-subject"
                    value={replySubject}
                    onChange={(e) => setReplySubject(e.target.value)}
                    placeholder="Subject line"
                    className="bg-white border border-[var(--line)] text-ink h-10 focus-visible:ring-brand focus-visible:ring-offset-0"
                  />
                  <Textarea
                    data-testid="inquiry-reply-text"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    rows={5}
                    placeholder={`Hi ${selected.name.split(" ")[0] || "there"}, thanks for reaching out…`}
                    className="bg-white border border-[var(--line)] text-ink focus-visible:ring-brand focus-visible:ring-offset-0"
                  />
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <p className="text-xs text-ink/55 leading-relaxed flex-1 min-w-[200px]">
                      Replies to this email land at <strong>info@itsthefreedominme.com</strong>. We log every send right above so you always have a paper trail.
                    </p>
                    <Button
                      type="button"
                      onClick={sendReply}
                      disabled={sendingReply || replyText.trim().length < 2}
                      data-testid="inquiry-reply-send"
                      className="btn-ink rounded-full h-10 px-5 shrink-0"
                    >
                      {sendingReply ? <Loader2 className="animate-spin" size={14} /> : <><Send size={13} className="mr-1.5" /> Send reply</>}
                    </Button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="inq_notes" className="text-[11px] uppercase tracking-[0.18em] text-ink/60 font-mono">Internal notes (only you see this)</Label>
                  <Textarea
                    id="inq_notes"
                    data-testid="inquiry-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                    placeholder="e.g. Followed up via Gmail on 6/5. Booked 1:1 for 6/10."
                    className="bg-bone border border-[var(--line)] text-ink mt-1.5 focus-visible:ring-brand focus-visible:ring-offset-0"
                  />
                </div>
              </div>

              <DialogFooter className="pt-4 gap-2 flex-wrap">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => deleteInquiry(selected.id)}
                  disabled={busy}
                  data-testid="inquiry-delete"
                  className="rounded-full h-10 px-4 border-red-200 text-red-700 hover:bg-red-50"
                >
                  <Trash2 size={13} className="mr-1.5" /> Delete
                </Button>
                <div className="flex-1" />
                {selected.status !== "archived" && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStatus(selected.id, "archived")}
                    disabled={busy}
                    data-testid="inquiry-archive"
                    className="btn-outline-ink rounded-full h-10 px-4"
                  >
                    <Archive size={13} className="mr-1.5" /> Archive
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={saveNotes}
                  disabled={busy}
                  data-testid="inquiry-save-notes"
                  className="btn-outline-ink rounded-full h-10 px-4"
                >
                  <Save size={13} className="mr-1.5" /> Save notes
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={close}
                  className="btn-outline-ink rounded-full h-10 px-4"
                >
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
